<template>
  <div class="photo-gallery-container">
    <div class="photo-gallery-content">
      <!-- Show missing count when multiple locations -->
      <div v-if="locations.length > 1" class="timestamp-badge">
        Locations missing Logo: {{ getMissingCount("logo") }}
        <br />
        Locations missing Cover Photo: {{ getMissingCount("coverPhoto") }}
      </div>
      <div v-else class="timestamp-badge">
        Current Media Count: {{ location?.mediaItems?.photosLength || 0 }}
      </div>
      <div class="info-icon">
        <div class="timestamp-icon">
          <v-icon x-small>mdi-information</v-icon>
        </div>
        <div class="timestamp-info">
          <span>Images should be less than 20MB. Supported formats: png, jpg, jpeg, heic, heif, webp</span>
        </div>
      </div>
      <!-- Logo Section -->
      <div class="section mb-4">
        <h3 class="section-title mb-2">LOGO</h3>
        <!-- Single location with logo -->
        <div v-if="locations.length === 1 && location?.mediaItems?.logo">
          <div class="d-flex justify-space-between align-center">
            <div
              class="image-preview-container"
              @mouseover="showPreview('logo')"
              @mouseleave="hidePreview"
            >
              <img
                :src="
                  currentImagePreviews.logo ||
                  logoImage
                "
                class="preview-image mr-4"
                alt="Logo"
              />

              <!-- Hover Preview -->
              <div v-if="previewType === 'logo'" class="image-hover-preview">
                <img
                  :src="
                    currentImagePreviews.logo ||
                    logoImage
                  "
                  class="preview-large"
                  alt="Logo Preview"
                />
              </div>
            </div>
            <v-btn color="primary" @click="initCloudinaryWidget('logo')">
              Update
            </v-btn>
          </div>
        </div>
        <!-- Multiple locations or no logo -->
        <div v-else class="d-flex justify-space-between align-center">
          <img
            :src="currentImagePreviews.logo || fallbackImage"
            class="preview-image mr-4"
            alt="No Logo"
          />
          <v-btn color="primary" @click="initCloudinaryWidget('logo')">
            Set
          </v-btn>
        </div>
      </div>

      <!-- Cover Photo Section -->
      <div class="section mb-6">
        <h3 class="section-title mb-2">COVER PHOTO</h3>
        <!-- Single location with cover photo -->
        <div v-if="locations.length === 1 && location?.mediaItems?.coverPhoto">
          <div class="d-flex justify-space-between align-center">
            <div
              class="image-preview-container"
              @mouseover="showPreview('cover')"
              @mouseleave="hidePreview"
            >
              <img
                :src="
                  currentImagePreviews.cover ||
                  coverImage
                "
                class="preview-image mr-4"
                alt="Cover Photo"
              />
              <!-- Hover Preview -->
              <div v-if="previewType === 'cover'" class="image-hover-preview">
                <img
                  :src="
                    currentImagePreviews.cover ||
                    coverImage
                  "
                  class="preview-large"
                  alt="Cover Preview"
                />
              </div>
            </div>
            <v-btn color="primary" @click="initCloudinaryWidget('cover')">
              Update
            </v-btn>
          </div>
        </div>
        <!-- Multiple locations or no cover photo -->
        <div v-else class="d-flex justify-space-between align-center">
          <img
            :src="currentImagePreviews.cover || fallbackImage"
            class="preview-image mr-4"
            alt="No Cover"
          />
          <v-btn color="primary" @click="initCloudinaryWidget('cover')">
            Set
          </v-btn>
        </div>
      </div>

      <!-- Add New Photo Section -->
      <div class="section">
        <h3 class="section-title mb-2">Add New Photo</h3>
        <v-btn color="primary" v-if="newPhotos.length<5" @click="initCloudinaryWidget('photo')">
          <span class="icon-plus">+</span>
          Add
        </v-btn>

        <!-- new thumbnails -->
        <!-- new thumbnails with category pickers -->
        <div v-if="newPhotos.length" class="d-flex mt-3 flex-wrap">
          <div
            v-for="(photo, idx) in newPhotos"
            :key="idx"
            class="preview-wrapper mr-4 mb-2"
          >
            <button class="remove-btn" @click="removePreview(idx)">✕</button>

            <img :src="photo.url" class="preview-image mb-1" alt="New photo preview" />

            <!-- category selector -->
            <select
              v-model="photo.category"
              class="category-select"
            >
              <option disabled value="">Select category</option>
              <option value="INTERIOR">INTERIOR</option>
              <option value="EXTERIOR">EXTERIOR</option>
              <option value="ADDITIONAL">ADDITIONAL</option>
            </select>
          </div>
        </div>

      </div>
      <!-- Update Results Dialog -->
      <v-dialog
        v-model="showUpdateResults"
        max-width="600"
        scrollable
        persistent
        :retain-focus="false"
      >
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>Update Results</span>
            <v-btn icon @click="closeUpdateResults"><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>

          <v-card-text>
            <div class="mb-4 flex gap-4">
              <v-chip outlined color="success">Success: {{ getSuccessCount(selectedLocation) }}</v-chip>
              <v-chip outlined color="error">Failed: {{ getFailedCount(selectedLocation) }}</v-chip>
            </div>

            <v-data-table
              :headers="[
                { text: 'Location', value: 'title' },
                { text: 'Field', value: 'field' },
                { text: 'Status', value: 'status' }
              ]"
              :items="selectedLocation?.updateResults ?? []"
              :items-per-page="10"
              dense
            >
              <template #item.field="{ item }">
                <span class="capitalize">{{ formatFieldName(item.field) }}</span>
              </template>
              <template #item.status="{ item }">
                <div class="flex items-center">
                  <v-chip small :color="item.status === 'success' ? 'success' : 'error'" text-color="white">
                    {{ item.status }}
                  </v-chip>
                  <v-tooltip v-if="item.status === 'failed' && item.error" bottom>
                    <template #activator="{ on }">
                      <v-icon small class="ml-1 cursor-pointer" v-on="on">mdi-information</v-icon>
                    </template>
                    <span>{{ getErrorMessage(item.error) }}</span>
                  </v-tooltip>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Update on Google button -->
      <div class="photo-gallery-footer" v-if="hasImageChanges">
        <v-btn :disabled="!allPhotosCategorised" @click="updatePhotosOnGoogle" :loading="isUpdating">
          <span class="flex items-center space-x-2 text-blue-500">
            <img src="@/assets/img/icons8-google-48.png" alt="Google Icon" class="w-5 h-5" />
            <span v-if="isBulk" class="font-bold">
              Update for {{ locations.length }} locations
            </span>
            <span v-else class="font-bold">
              Update on Google
            </span>
          </span>
        </v-btn>
      </div>

      <!-- Image Cropper -->
      <image-cropper-grid
        ref="imageCropper"
        :items="imagesToCrop"
        @onImageCropped="onImageCropped"
        @discard="discardImages"
      />
    </div>
  </div>
</template>

<script>
import { httpsCallable } from "firebase/functions";
import { doc, updateDoc, addDoc, collection } from "firebase/firestore";
import { auth, functions, db } from "@/utils/firebase.utils";
import ImageCropperGrid from "@/components/fly/ImageCropperGrid.vue";
import { GMB_ERROR_MESSAGES } from "@/utils/common.utils";

export default {
  components: {
    ImageCropperGrid,
  },
  props: {
    locations: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      uploadedBatch: [],
      newPhotos: [], 
      isFetchingPhotos: false,
      isUpdating: false,
      showUpdateResults: false,
      updateResults: [],
      previewType: null,
      currentUploadType: null,
      imagesToCrop: [],
      currentImagePreviews: {
        logo: null,
        cover: null,
      },
      updatedMedia: {
        logo: null,
        cover: null,
        photos: []    // array of new photo URLs
      },
      cloudinaryWidget: null,
      showUpdateResults : false,
      selectedLocation  : null,   
      changes: [], 
    };
  },
  computed: {
    logoImage() {
      return this.locations[0].mediaItems.logo.thumbnailUrl;
    },
    coverImage() {
      return this.locations[0].mediaItems.coverPhoto.thumbnailUrl;
    },
    fallbackImage() {
      return require('@/assets/img/no-image.svg');
    },
    isBulk () {                       // <—  NEW
      return this.locations.length > 1;
    },
    location () {                     // already there – unchanged
      return this.locations.length === 1 ? this.locations[0] : null;
    },
    allPhotosCategorised() {
      return this.newPhotos.every(p => p.category);
    },
    hasImageChanges() {
      return (
        this.updatedMedia.logo ||
        this.updatedMedia.cover ||
        this.updatedMedia.photos.length > 0
      );
    },
    location() {
      return this.locations.length === 1 ? this.locations[0] : null;
    },
    currentUID() {
      return this.$store.getters.currentUID;
    },
    WID() {
      return this.$store.getters.WID;
    },
  },
  mounted() {
    this.fetchAttributes();
    this.loadCloudinaryWidget();
  },
  methods: {
    getSuccessCount(loc) { return (loc?.updateResults || []).filter(r => r.status==='success').length; },
    getFailedCount (loc) { return (loc?.updateResults || []).filter(r => r.status==='failed' ).length; },
    formatFieldName(field) {
      const map = {
        "media.logo": "Logo",
        "media.coverPhoto": "Cover Photo",
        "media.photos": "Photo"
      }
      const key = (field || "").toString();
      if (map[key]) return map[key];
      return key.replace(/_/g," ").replace(/([a-z])([A-Z])/g,"$1 $2")
              .replace(/^\w/,c=>c.toUpperCase());
    }, 
    mapMediaField(photo) {
      if (photo.kind === 'logo')       return 'MEDIA – Logo';
      if (photo.kind === 'coverPhoto') return 'MEDIA – Cover Photo';
      return `MEDIA – Photo (${photo.category})`;
    },
    getErrorMessage(code) {
      return GMB_ERROR_MESSAGES[code] || "Unknown error";
    }, 
    showUpdateResultsForLocation(loc) {
      this.selectedLocation = loc;
      this.showUpdateResults = true;
    },
    closeUpdateResults() {
      this.showUpdateResults = false;
      this.selectedLocation  = null;
    },
    removePreview(idx) {
      this.newPhotos.splice(idx, 1);
      this.updatedMedia.photos.splice(idx, 1);
    },
    fetchAttributes() {
      console.log("This is the data:", this.locations);
    },
    getMissingCount(type) {
      return this.locations.filter((loc) => !loc?.mediaItems?.[type]).length;
    },
    closeDialog() {
      this.$emit("update:show", false);
    },
    loadCloudinaryWidget() {
      if (!window.cloudinary) {
        const script = document.createElement("script");
        script.src = "https://upload-widget.cloudinary.com/global/all.js";
        script.onload = () => {
          console.log("Cloudinary widget loaded");
        };
        document.head.appendChild(script);
      }
    },
    initCloudinaryWidget(type) {
      if (!window.cloudinary) {
        this.loadCloudinaryWidget();
        setTimeout(() => this.initCloudinaryWidget(type), 1000);
        return;
      }

      this.currentUploadType = type;
      let uid = this.currentUID;
      // allow multi-select only when the caller says so
      const allowMultiple = type === "photo";

      this.cloudinaryWidget = window.cloudinary.createUploadWidget(
        {
          cloudName: process.env.VUE_APP_CLOUDINARY_CLOUD_NAME,
          uploadPreset: "demo_preset",
          sources: [
            "local",
            "camera",
            "url",
            "dropbox",
            "image_search",
            "shutterstock",
            "gettyimages",
            "istock",
            "unsplash",
            "google_drive",
          ],
          accept: "image/*",
          clientAllowedFormats: ["png", "jpg", "jpeg", "heic", "heif", "webp"],
          // 👇  key lines
          multiple : allowMultiple,                  // enable multi-select for “photo”
          maxFiles : allowMultiple ? 5 : 1,          // hard cap of five
          maxImageFileSize: 20000000,
          tags: [`${uid}_${this.WID}`],
          folder: `users/gmb_media_photo/${this.currentUID}/${this.WID}`,
        },
        (error, result) => {
          if (!error && result && result.event === "success") {
            // console.log("Upload successful:", result.info, type);
            this.uploadedBatch.push(result.info.secure_url);
          }
          if (!error && result && result.event === "close") {
            if (this.uploadedBatch.length) {
                this.openCropper(type);          // ⬅︎ open cropper once
            }
          }
        }
      );

      this.cloudinaryWidget.open();
    },
    openCropper(type) {
      this.imagesToCrop  = [...this.uploadedBatch];
      this.uploadedBatch = [];

      /* aspect-ratio presets ---------------------------------- */
      const PRESETS = {
        square    : {title:'Square',     value:{width:1,height:1},  icon:'mdi-crop-square'},
        landscape : {title:'Landscape',  value:{width:4,height:3},  icon:'mdi-crop-landscape'},
        widescreen: {title:'Widescreen', value:{width:16,height:9}, icon:'mdi-crop-free'},
      };
      let ratios = (type === 'cover')
                    ? [PRESETS.widescreen]
                    : (type === 'logo')
                        ? [PRESETS.square, PRESETS.landscape]
                        : Object.values(PRESETS);

      this.$nextTick(() => {
        const c = this.$refs.imageCropper;
        if (c) {
          c.aspectRatios = ratios;
          c.dialog       = true;
        }
      });
    },
    handleBatch(type) {
      // put URLs into reactive prop the cropper expects
      this.imagesToCrop = [...this.uploadedBatch];
      this.uploadedBatch = [];                // reset for next session
      this.handleUploadSuccess(null, type);   // just to pick ratio & open
    },
    handleUploadSuccess(type) {   
      // console.log("Upload info:",type);
      this.imagesToCrop  = [ ...this.uploadedBatch ];

      // Set the images to crop with the uploaded image
      // if (type === "photo") {                  // multi-select branch
      //   // initialise once
      //   if (!Array.isArray(this.imagesToCrop)) this.imagesToCrop = [];
      //   this.imagesToCrop.push(uploadInfo.secure_url);   // append

      // } else {                                // single-file branches
      //   this.imagesToCrop = [uploadInfo.secure_url];
      // }

      // 2.  Build one canonical list of ratios ------------------------------
      const RATIO_PRESETS = {
        square: {
          title: "Square",
          value: { width: 1, height: 1 },
          icon: "mdi-crop-square",
        },
        landscape: {
          title: "Landscape",
          value: { width: 4, height: 3 },
          icon: "mdi-crop-landscape",
        },
        widescreen: {
          title: "Widescreen",
          value: { width: 16, height: 9 },
          icon: "mdi-crop-free",
        },
      };

       // 3.  Decide which ones to expose -------------------------------------
      let ratiosToUse;
      if (type === "cover") {
        // Cover → only 16:9
        ratiosToUse = [RATIO_PRESETS.widescreen];
      } else if (type === "logo") {
        // Logo → 1:1 and 4:3
        ratiosToUse = [RATIO_PRESETS.square, RATIO_PRESETS.landscape];
      } else {
        // Everything else → full list
        ratiosToUse = [
          RATIO_PRESETS.square,
          RATIO_PRESETS.landscape,
          RATIO_PRESETS.widescreen,
        ];
      }

      // 4.  Open the cropper with the filtered list -------------------------
      this.$nextTick(() => {
        const cropper = this.$refs.imageCropper;
        if (cropper) {
          cropper.aspectRatios = ratiosToUse;
          cropper.dialog = true;
          this.imageCropperOpened = true;
        }
      });
    },
    onImageCropped(cropped) {
      if (!cropped?.length) return;

      const urls = cropped.map(u => typeof u === 'string'
                                    ? u
                                    : u.secure_url || u.url || '');

      if (this.currentUploadType === 'photo') {
        const objects = urls.map(u => ({ url: u, category: '' }));
        this.newPhotos.push(...objects);
        this.updatedMedia.photos.push(...objects);
      } else {
        this.$set(this.currentImagePreviews, this.currentUploadType, urls[0]);
        this.updatedMedia[this.currentUploadType] = urls[0]; // logo or cover
      }


      /* reset state ------------------------------------------ */
      this.imagesToCrop      = [];
      this.currentUploadType = null;
    },
    discardImages() {
      // console.log("Images discarded");
      this.imagesToCrop = [];
      this.uploadedBatch = [];
      if (this.currentUploadType === 'photo') this.newPhotos = [];
      this.currentUploadType = null;
    },
    showPreview(type) {
      if (this.locations.length === 1) {
        this.previewType = type;
      }
    },
    hidePreview() {
      this.previewType = null;
    },
    getLocationName(location) {
      return `${location.title} ${
        location?.storefrontAddress?.addressLines?.[0]?.split(",")[0]
      },
                                                ${
                                                  location?.storefrontAddress
                                                    ?.locality
                                                } ,
                                                ${
                                                  location?.storefrontAddress
                                                    ?.administrativeArea
                                                }`;
    },
    async updatePhotosOnGoogle () {
      try {
        this.isUpdating = true;
        this.changes    = [];

        /* 1️⃣  build the media payload exactly once  ------------------------- */
        const media = {};
        if (this.updatedMedia.logo) {
          media.logo = this.updatedMedia.logo;
          this.changes.push({ id:'media.logo', field:'Logo',
                              old:this.location?.mediaItems?.logo?.googleUrl || '',
                              new:this.updatedMedia.logo });
        }
        if (this.updatedMedia.cover) {
          media.coverPhoto = this.updatedMedia.cover;
          this.changes.push({ id:'media.coverPhoto', field:'Cover Photo',
                              old:this.location?.mediaItems?.coverPhoto?.googleUrl || '',
                              new:this.updatedMedia.cover });
        }
        if (this.updatedMedia.photos.length) {
          media.photos = this.updatedMedia.photos.map(p => ({ url:p.url, category:p.category }));
          this.updatedMedia.photos.forEach(p =>
            this.changes.push({ id:`media.photo.${p.url}`,
                                field:`Photo (${p.category})`,
                                old:'', new:p.url }));
        }
        if (!Object.keys(media).length) {
          this.$snackbar.show('No changes to update');
          return;
        }

        /* 2️⃣  build the updates[] array – one entry per location ------------ */
        const updates = this.locations.map(loc => ({
          locationId  : loc.name,
          displayName : loc.title || '',
          wid         : loc.workspaceId,
          accountId   : loc.accountId,
          updateFields: { media }
        }));

        const payload = {
          uid         : this.currentUID,
          workspace_id: this.WID,
          updates
        };
        // console.log("This is the payload ", payload);
        const callCF  = httpsCallable(functions, 'updateGMBDataFunction');
        const res     = await callCF(payload);
        console.log("Got these results ", res);

        /* 3️⃣  massage results so UI/dialog work for BOTH single & bulk ------ */
        if (res?.data?.results?.length) {
          const locationMap = {};
          this.locations.forEach(l => {
            locationMap[l.workspaceId] = this.getLocationName(l);
          });

          const fresh = res.data.results.map(r => ({
            ...r,
            title: locationMap[r.wid] || 'Unknown Location'
          }));

          // single-location flow keeps its per-location store
          if (!this.isBulk) {
            const byField = new Map((this.location.updateResults || [])
                                    .map(x => [x.field, x]));
            fresh.forEach(x => byField.set(x.field, x));
            this.location.updateResults = Array.from(byField.values());
          }

          // global store (used in accordion etc.)
          this.updateResults.push(...fresh);

          /* 4️⃣  history ------------------------------------------------------ */
          await addDoc(
            collection(db,
              'users', this.currentUID,
              'fly_workspaces', this.WID,
              'gmb-update-history'),
            {
              requested_by : auth.currentUser.displayName || 'unknown',
              type         : this.isBulk ? 'multiple' : 'single',
              title        : this.isBulk
                              ? 'Request for bulk Update'
                              : this.location.title,
              locationId   : this.isBulk ? '' : this.location.name,
              wid          : this.isBulk ? '' : this.location.workspaceId,
              accountId    : this.isBulk ? '' : (this.location.accountId || ''),
              address      : this.isBulk
                              ? ''
                              : this.location.storefrontAddress?.addressLines?.[0] || '',
              changes      : this.changes,
              updateResults: fresh,
              requested_at : new Date()
            }
          );
          /* 5️⃣  dialog + snackbar ------------------------------------------- */
          if (this.isBulk) {
            // reuse existing dialog component – just stuff all rows in
            this.selectedLocation  = { updateResults: fresh };
            this.showUpdateResults = true;
          } else {
            this.showUpdateResultsForLocation(this.location);
          }

          this.$snackbar.show(
            `Update finished: ✓ ${fresh.filter(x=>x.status==='success').length}
            ✗ ${fresh.filter(x=>x.status==='failed' ).length}`
          );

          /* 6️⃣  reset local state ------------------------------------------- */
          this.updatedMedia = { logo:null, cover:null, photos:[] };
          this.newPhotos    = [];
        }

      } catch (err) {
        console.error(err);
        this.$alert.show('Failed to update images on Google');
      } finally {
        this.isUpdating = false;
      }
    },
    // Keep the existing file upload handler for backward compatibility
    async handleFileUpload(event, type) {
      const file = event.target.files[0];
      if (!file) return;

      try {
        // console.log(`Uploading ${type} file:`, file);
        this.$emit("upload", { type, file });
        event.target.value = "";
      } catch (error) {
        console.error("Error uploading file:", error);
      }
    },
    triggerFileInput(type) {
      // This method is kept for backward compatibility but now redirects to Cloudinary
      this.initCloudinaryWidget(type);
    },
  },
};
</script>

<style scoped>
.photo-gallery-container {
  /* max-width: 800px; */
  background: white;
  border-radius: 4px;
  /* box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); */
  padding: 18px;
  margin: 0 auto;
}

.photo-gallery-header {
  margin-bottom: 24px;
}

.headline {
  font-size: 24px;
  font-weight: 500;
  margin: 0;
}

.photo-gallery-content {
  padding: 0 0 24px;
}

.section {
  margin-bottom: 24px;
}

.section-title {
  font-size: 18px;
  font-weight: 500;
  margin: 0 0 16px;
}

.d-flex {
  display: flex;
}

.justify-space-between {
  justify-content: space-between;
}

.align-center {
  align-items: center;
}

.mb-2 {
  margin-bottom: 8px;
}

.mb-4 {
  margin-bottom: 16px;
}

.mb-6 {
  margin-bottom: 24px;
}

.mr-4 {
  margin-right: 16px;
}

.preview-image {
  max-width: 100px;
  height: auto;
  object-fit: contain;
}

.preview-large {
  max-width: 300px;
  height: auto;
  object-fit: contain;
}

.btn-primary {
  background: #1976d2;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background 0.2s;
}

.btn-primary:hover {
  background: #1565c0;
}

.btn-secondary {
  background: transparent;
  color: #424242;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background 0.2s;
}

.btn-secondary:hover {
  background: rgba(0, 0, 0, 0.05);
}

.hidden {
  display: none;
}

.image-preview-container {
  position: relative;
  cursor: pointer;
}

.image-hover-preview {
  position: absolute;
  top: -20px;
  left: 100px;
  z-index: 999;
  background: white;
  padding: 8px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.icon-plus {
  margin-right: 8px;
}

.text-subtitle-1 {
  font-size: 16px;
  color: rgba(0, 0, 0, 0.6);
}
.category-select {
  width: 100%;
  font-size: 12px;
  margin-top: 4px;
}

.photo-gallery-footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}
.timestamp-badge {
  display: flex;
  align-items: center;
  background-color: #f8fafc;
  border-radius: 4px;
  padding: 3px 6px;
  max-width: fit-content;
  border-left: 2px solid #818cf8;
  margin-bottom: 8px;
}
.info-icon {
  display: flex;
  align-items: center;
  background-color: #f8fafc;
  border-radius: 4px;
  padding: 3px 6px;
  max-width: fit-content;
  border-left: 2px solid #ffb53f;
  margin-bottom: 8px;
}
.timestamp-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 3px;
  color: #64748b;
}
.timestamp-info {
  display: flex;
  align-items: center;
  font-size: 0.65rem;
}
</style>
